import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { businessListingsApi } from "../../lib/businessListings";
import type {
  BusinessListingApplicationMessageRequest,
  BusinessListingApplicationMessageResponse,
  BusinessListingApplicationMessageSenderRole,
} from "../../types/businessListingTypes";
import { formatDateTime } from "../../utils/DateUtils";
import { apiErrorToMessage } from "../../utils/ApiUtils";
import Button from "../ui/Button/Button";
import clsx from "clsx";

type BusinessListingApplicationMessagesPanelProps = {
  applicationId: string;
  role: "teen" | "business";
  participantLabel: string;
  description?: string;
  variant?: "card" | "flat" | "bare";
  fullHeight?: boolean;
};

const ownSenderRoleByViewer: Record<BusinessListingApplicationMessagesPanelProps["role"], BusinessListingApplicationMessageSenderRole> = {
  teen: "TEEN",
  business: "BUSINESS",
};

const apiByRole = {
  teen: {
    list: businessListingsApi.listApplicationMessagesForTeen,
    send: businessListingsApi.sendApplicationMessageAsTeen,
  },
  business: {
    list: businessListingsApi.listApplicationMessagesForBusiness,
    send: businessListingsApi.sendApplicationMessageAsBusiness,
  },
} satisfies Record<BusinessListingApplicationMessagesPanelProps["role"], {
  list: (applicationId: string) => Promise<BusinessListingApplicationMessageResponse[]>;
  send: (applicationId: string, payload: BusinessListingApplicationMessageRequest) => Promise<BusinessListingApplicationMessageResponse>;
}>;

const BusinessListingApplicationMessagesPanel = ({
  applicationId,
  role,
  participantLabel,
  description,
  variant = "card",
  fullHeight = false,
}: BusinessListingApplicationMessagesPanelProps) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const previousMessageCountRef = useRef(0);

  const messagesQuery = useQuery({
    queryKey: ["business-listing-application-messages", role, applicationId],
    queryFn: () => apiByRole[role].list(applicationId),
    refetchInterval: 15000,
  });

  const sendMutation = useMutation({
    mutationFn: (payload: BusinessListingApplicationMessageRequest) => apiByRole[role].send(applicationId, payload),
    onSuccess: async () => {
      setDraft("");
      setSubmissionError(null);
      await queryClient.invalidateQueries({ queryKey: ["business-listing-application-messages", role, applicationId] });
    },
    onError: (error) => {
      setSubmissionError(apiErrorToMessage(error, "Unable to send that message."));
    },
  });

  const ownSenderRole = ownSenderRoleByViewer[role];
  const messages = messagesQuery.data ?? [];
  const trimmedDraft = draft.trim();

  useEffect(() => {
    previousMessageCountRef.current = 0;
  }, [applicationId, role]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      previousMessageCountRef.current = messages.length;
      return;
    }
    if (messages.length > previousMessageCountRef.current) {
      const animationFrame = window.requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
      previousMessageCountRef.current = messages.length;
      return () => window.cancelAnimationFrame(animationFrame);
    }
    previousMessageCountRef.current = messages.length;
    return undefined;
  }, [messages]);

  return (
    <section
      className={clsx(
        "min-w-0 overflow-hidden rounded-2xl",
        fullHeight && "flex h-full flex-col",
        variant === "card" && "border border-myteengig-border bg-white px-4 py-4",
        variant === "flat" && "bg-myteengig-background/60 p-5",
        variant === "bare" && "bg-transparent",
      )}
    >
      <div className="flex justify-center text-center">
        <div>
          <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Messages</p>
          <p className="mt-1 text-sm text-myteengig-text-muted">
            {description ?? `Keep communication with the ${participantLabel} inside MyTeenGig until you are ready to continue offline.`}
          </p>
        </div>
      </div>

      <div
        ref={messagesContainerRef}
        className={clsx(
          "mt-3 space-y-2 overflow-x-hidden overflow-y-auto rounded-2xl bg-myteengig-background p-1",
          fullHeight ? "min-h-0 flex-1" : "max-h-80",
        )}
      >
        {messagesQuery.isLoading ? <p className="text-sm text-myteengig-text-muted">Loading messages...</p> : null}
        {messagesQuery.error ? (
          <p className="text-sm text-red-600">{apiErrorToMessage(messagesQuery.error, "Failed to load messages.")}</p>
        ) : null}
        {!messagesQuery.isLoading && !messagesQuery.error && messages.length === 0 ? (
          <p className="text-sm text-myteengig-text-muted">No messages yet. Start the conversation when you are ready.</p>
        ) : null}

        {messages.map((message) => {
          const isOwn = message.senderRole === ownSenderRole;
          return (
            <article
              key={message.id}
              className={clsx(
                "min-w-0 rounded-2xl px-4 py-3",
                isOwn
                  ? "ml-auto max-w-[90%] border border-lime-200 bg-lime-50"
                  : "mr-auto max-w-[90%] border border-myteengig-border bg-white"
              )}
            >
              <div className="flex flex-col gap-y-1 text-xs text-myteengig-text-muted sm:flex-row sm:items-center sm:justify-between sm:gap-x-2">
                <span className="break-words font-semibold uppercase tracking-wide text-myteengig-text-primary">
                  {isOwn ? "You" : message.senderDisplayName}
                </span>
                <span>{formatDateTime(message.createdAt)}</span>
              </div>
              <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-myteengig-text-primary">{message.body}</p>
            </article>
          );
        })}
      </div>

      <div className="mt-4">
        <label htmlFor={`business-listing-message-draft-${role}-${applicationId}`} className="text-sm font-medium text-myteengig-text-primary">
          New message
        </label>
        <textarea
          id={`business-listing-message-draft-${role}-${applicationId}`}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            if (submissionError) {
              setSubmissionError(null);
            }
          }}
          rows={4}
          maxLength={5000}
          className={clsx(
            "mt-2 w-full rounded-2xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none transition focus:border-myteengig-primary",
            (variant === "flat" || variant === "bare") && "shadow-sm",
          )}
          placeholder={`Message the ${participantLabel}.`}
        />
        {submissionError ? <p className="mt-2 text-sm text-red-600">{submissionError}</p> : null}
        <div className="mt-3 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-myteengig-text-muted">{trimmedDraft.length}/5000</p>
          <Button
            className="w-full sm:w-auto"
            disabled={sendMutation.isPending || trimmedDraft.length === 0}
            onClick={() => {
              void sendMutation.mutateAsync({ message: trimmedDraft });
            }}
          >
            {sendMutation.isPending ? "Sending..." : "Send message"}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BusinessListingApplicationMessagesPanel;
